// 每路錄影健康（SPEC #vms-web-rec-health、#storage-rec-health）：通道子 Thing reported 的 kinio_recording.rec_health
// {state: ok | idle | interrupted, reason?, since, last_write_at?, frames_dropped_1h, fsync_ms_p95_1h?, fsync_ms_max_1h?, write_errors_24h}。

export type RecHealthState = 'ok' | 'idle' | 'interrupted';

export interface RecHealth {
  state: RecHealthState;
  /** interrupted 才有；值域同 protocol（disk_repairing / no_storage / no_space / io_error / no_stream / unsupported_stream / not_writing） */
  reason?: string;
  /** 這個狀態開始的時間（epoch 秒） */
  since?: number;
  lastWriteAt?: number;
  framesDropped1h?: number;
  fsyncP95?: number;
  fsyncMax?: number;
  writeErrors24h?: number;
}

/** 認得的中斷原因（白話在 i18n rech.reason_*；不認得的寫「原因不明」）。 */
export const REC_HEALTH_REASONS = ['disk_repairing', 'no_storage', 'no_space', 'io_error', 'no_stream', 'unsupported_stream', 'not_writing'] as const;

const num = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);

/** shadow reported → rec_health；沒有（舊韌體、還沒連上）或形狀不對 → null（畫面寫「沒有回報」）。 */
export function recHealthOf(reported: Record<string, unknown> | null | undefined): RecHealth | null {
  const r = ((reported?.kinio_recording ?? null) as { rec_health?: unknown } | null)?.rec_health as Record<string, unknown> | undefined;
  if (!r || typeof r !== 'object') return null;
  const state = r.state;
  if (state !== 'ok' && state !== 'idle' && state !== 'interrupted') return null;
  return {
    state,
    reason: state === 'interrupted' && typeof r.reason === 'string' && r.reason ? r.reason : undefined,
    since: num(r.since),
    lastWriteAt: num(r.last_write_at),
    framesDropped1h: num(r.frames_dropped_1h),
    fsyncP95: num(r.fsync_ms_p95_1h),
    fsyncMax: num(r.fsync_ms_max_1h),
    writeErrors24h: num(r.write_errors_24h),
  };
}

/** 中斷原因的 i18n key（不認得的 → reason_unknown）。 */
export function reasonKey(reason: string | undefined): string {
  return (REC_HEALTH_REASONS as readonly string[]).includes(reason ?? '') ? `rech.reason_${reason}` : 'rech.reason_unknown';
}

/** 主機表的排序：中斷的在前，再沒回報、正常、不用錄；同組照通道順序。 */
export function recHealthRank(h: RecHealth | null | undefined): number {
  if (!h) return 1;
  return h.state === 'interrupted' ? 0 : h.state === 'ok' ? 2 : 3;
}
